const root = document.documentElement;
const form = document.querySelector('[data-autosubmit]');
const searchInput = document.getElementById('search');

/**
 * Submit the filter form as soon as a chip, segment or select changes,
 * dimming the results so it is clear that new listings are on the way.
 */
if (form) {
    form.addEventListener('change', (event) => {
        const target = event.target;

        // Multi-select chips: "Alla" clears the group, and an empty group falls back to "Alla".
        if (target.matches('input[type="checkbox"]')) {
            const group = target.dataset.filterAll ?? target.name.replace('[]', '');
            const allBox = form.querySelector(`[data-filter-all="${group}"]`);
            const options = [...form.querySelectorAll(`input[name="${group}[]"]`)];

            if (target === allBox) {
                options.forEach((option) => {
                    option.checked = false;
                });
                allBox.checked = true;
            } else {
                allBox.checked = !options.some((option) => option.checked);
            }
        }

        if (target.matches('input[type="radio"], input[type="checkbox"], select, input[name="near"]')) {
            form.requestSubmit();
        }
    });

    form.addEventListener('submit', () => {
        document.querySelector('[data-results]')?.classList.add('opacity-40', 'pointer-events-none');
        form.querySelector('[data-spinner]')?.classList.remove('hidden');
    });
}

/**
 * The map view loads Leaflet and its own script only when first shown.
 */
const mapElement = document.querySelector('[data-map]');
let mapRequested = false;

const ensureMap = () => {
    if (!mapElement || mapRequested) {
        return;
    }
    mapRequested = true;
    const script = document.createElement('script');
    script.src = mapElement.dataset.mapScript;
    script.defer = true;
    document.head.appendChild(script);
};

/**
 * Grid, narrow, list or map view, remembered between visits.
 */
const setView = (view) => {
    root.dataset.view = view;
    if (view === 'map') {
        ensureMap();
        window.dispatchEvent(new Event('syntpris:map-shown'));
    }
    try {
        localStorage.setItem('syntpris:view', view);
    } catch (e) {}
    document.querySelectorAll('[data-view-toggle]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.viewToggle === view));
    });
};

document.querySelectorAll('[data-view-toggle]').forEach((button) => {
    button.addEventListener('click', () => setView(button.dataset.viewToggle));
});
setView(root.dataset.view || 'grid');

/**
 * Press "/" anywhere to jump to the search field.
 */
document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || !searchInput || event.target.closest('input, textarea, select')) {
        return;
    }
    event.preventDefault();
    searchInput.focus();
    searchInput.select();
});

searchInput?.addEventListener('focus', () => document.querySelector('[data-search-hint]')?.classList.add('sm:hidden'));
searchInput?.addEventListener('blur', () => document.querySelector('[data-search-hint]')?.classList.remove('sm:hidden'));

/**
 * Product suggestions under the search field. Picking one opens its product page;
 * Enter without a picked suggestion searches the listings with the text as before.
 */
const suggestionList = document.getElementById('search-suggestions');
let closeSuggestions = () => {};

if (searchInput && suggestionList && searchInput.dataset.suggestUrl) {
    let options = [];
    let activeIndex = -1;
    let request = null;
    let debounce = null;

    closeSuggestions = () => {
        suggestionList.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
        searchInput.removeAttribute('aria-activedescendant');
        activeIndex = -1;
    };

    const setActive = (index) => {
        activeIndex = index;
        options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
        if (index >= 0) {
            searchInput.setAttribute('aria-activedescendant', options[index].id);
            options[index].scrollIntoView({ block: 'nearest' });
        } else {
            searchInput.removeAttribute('aria-activedescendant');
        }
    };

    const choose = (option) => {
        if (option.dataset.url) {
            window.location.href = option.dataset.url;
        } else {
            closeSuggestions();
            form.requestSubmit();
        }
    };

    const option = (id, url, children) => {
        const item = document.createElement('li');
        item.id = id;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.className = 'flex cursor-pointer items-center gap-3 px-3.5 py-2 aria-selected:bg-ink-100 dark:aria-selected:bg-ink-800';
        if (url) {
            item.dataset.url = url;
        }
        item.append(...children);
        item.addEventListener('mousedown', (event) => event.preventDefault());
        item.addEventListener('click', () => choose(item));
        item.addEventListener('mousemove', () => setActive(options.indexOf(item)));
        return item;
    };

    const text = (content, className) => {
        const element = document.createElement('span');
        element.className = className;
        element.textContent = content;
        return element;
    };

    const render = (products, query) => {
        options = products.map((product, index) => {
            const name = document.createElement('span');
            name.className = 'flex min-w-0 flex-1 flex-col';
            name.append(text(product.brand, 'truncate text-xs text-ink-500 dark:text-ink-400'), text(product.model, 'break-words font-medium leading-snug text-ink-900 dark:text-white'));
            const details = text(product.price ?? `${product.listings} ${product.listings === 1 ? 'annons' : 'annonser'}`, 'shrink-0 tabular-nums text-xs text-ink-500 dark:text-ink-400');
            return option(`search-suggestion-${index}`, product.url, [name, details]);
        });
        options.push(option('search-suggestion-listings', null, [
            text(`Sök annonser efter ”${query}”`, 'min-w-0 flex-1 truncate text-ink-600 dark:text-ink-300'),
            text('Enter', 'shrink-0 rounded-md border border-ink-200 px-1.5 py-0.5 text-xs text-ink-400 dark:border-ink-700'),
        ]));

        suggestionList.replaceChildren(...options);
        suggestionList.hidden = products.length === 0;
        searchInput.setAttribute('aria-expanded', String(products.length > 0));
        setActive(-1);
    };

    const fetchSuggestions = async () => {
        const query = searchInput.value.trim();
        request?.abort();
        if (query.length < 2) {
            closeSuggestions();
            return;
        }

        request = new AbortController();
        try {
            const response = await fetch(`${searchInput.dataset.suggestUrl}?q=${encodeURIComponent(query)}`, { signal: request.signal });
            render(await response.json(), query);
        } catch (e) {
            if (e.name !== 'AbortError') {
                closeSuggestions();
            }
        }
    };

    searchInput.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(fetchSuggestions, 150);
    });

    searchInput.addEventListener('keydown', (event) => {
        const open = !suggestionList.hidden;
        if (event.key === 'ArrowDown' && open) {
            event.preventDefault();
            setActive((activeIndex + 1) % options.length);
        } else if (event.key === 'ArrowUp' && open) {
            event.preventDefault();
            setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
        } else if (event.key === 'Enter' && open && activeIndex >= 0) {
            event.preventDefault();
            choose(options[activeIndex]);
        } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            closeSuggestions();
        }
    });

    searchInput.addEventListener('blur', closeSuggestions);
    form?.addEventListener('submit', closeSuggestions);
}

/**
 * Restore the results if the page is shown again from the back/forward cache.
 */
window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        closeSuggestions();
        document.querySelector('[data-results]')?.classList.remove('opacity-40', 'pointer-events-none');
        form?.querySelector('[data-spinner]')?.classList.add('hidden');
    }
});

/**
 * "Nära mig": the browser's position, rounded to about 10 km before it is sent.
 */
const nearInput = document.getElementById('near');
const nearLat = document.querySelector('[data-near-lat]');
const nearLng = document.querySelector('[data-near-lng]');
const locateButton = document.querySelector('[data-locate]');
const locateError = document.querySelector('[data-locate-error]');

nearInput?.addEventListener('input', () => {
    nearLat.value = '';
    nearLng.value = '';
});

locateButton?.addEventListener('click', () => {
    const showError = (message) => {
        locateError.textContent = message;
        locateError.classList.remove('hidden');
    };

    if (!navigator.geolocation) {
        showError('Din webbläsare kan inte dela position. Skriv en ort i stället.');
        return;
    }

    locateButton.disabled = true;
    navigator.geolocation.getCurrentPosition(
        (position) => {
            nearLat.value = position.coords.latitude.toFixed(1);
            nearLng.value = position.coords.longitude.toFixed(1);
            nearInput.value = '';
            form.requestSubmit();
        },
        () => {
            locateButton.disabled = false;
            showError('Kunde inte hämta din position. Tillåt platsåtkomst eller skriv en ort.');
        },
        { maximumAge: 600000, timeout: 10000 }
    );
});

/**
 * Collapsible filter panel. Its open state survives the reload after a filter
 * auto-submits, so several filters can be picked in a row.
 */
const filterPanel = document.querySelector('[data-filter-panel]');
const filterToggle = document.querySelector('[data-filter-toggle]');

const setFiltersOpen = (open) => {
    filterPanel.hidden = !open;
    filterToggle.setAttribute('aria-expanded', String(open));
    try {
        sessionStorage.setItem('syntpris:filters', open ? 'open' : 'closed');
    } catch (e) {}
};

if (filterPanel && filterToggle) {
    filterToggle.setAttribute('aria-expanded', String(!filterPanel.hidden));
    filterToggle.addEventListener('click', () => setFiltersOpen(filterPanel.hidden));
}

/**
 * Cookie consent. Google Analytics is only loaded after the visitor accepts,
 * and the choice is asked for again after a year.
 */
const consentKey = 'syntpris:consent';
const consentMaxAge = 365 * 24 * 60 * 60 * 1000;
const consentBanner = document.querySelector('[data-consent-banner]');
const analyticsId = document.querySelector('meta[name="ga-id"]')?.content;

/**
 * The consent dialog blocks the page until the visitor has chosen; both choices are equally easy.
 */
const showConsent = () => {
    if (!consentBanner) {
        return;
    }
    consentBanner.hidden = false;
    root.classList.add('overflow-hidden');
    // Focus the dialog itself so neither choice looks preselected.
    consentBanner.querySelector('[data-consent-panel]')?.focus();
};

const hideConsent = () => {
    if (!consentBanner) {
        return;
    }
    consentBanner.hidden = true;
    root.classList.remove('overflow-hidden');
};

consentBanner?.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') {
        return;
    }
    const focusable = [...consentBanner.querySelectorAll('a[href], button')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement.matches('[data-consent-panel]'))) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
});

const readConsent = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(consentKey));
        return stored && Date.now() - stored.at < consentMaxAge ? stored.value : null;
    } catch (e) {
        return null;
    }
};

const loadAnalytics = () => {
    if (!analyticsId || window.gtag) {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
        window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', analyticsId);

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
    document.head.appendChild(script);
};

const removeAnalyticsCookies = () => {
    const domains = ['', location.hostname, `.${location.hostname.replace(/^www\./, '')}`];
    document.cookie.split(';').map((cookie) => cookie.split('=')[0].trim()).filter((name) => name.startsWith('_ga')).forEach((name) => {
        domains.forEach((domain) => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
        });
    });
};

const saveConsent = (value) => {
    try {
        localStorage.setItem(consentKey, JSON.stringify({ value, at: Date.now() }));
    } catch (e) {}

    hideConsent();

    if (value === 'granted') {
        loadAnalytics();
    } else if (window.gtag) {
        // Analytics was running on this page; reload so it stops, after clearing its cookies.
        removeAnalyticsCookies();
        location.reload();
    } else {
        removeAnalyticsCookies();
    }
};

document.querySelectorAll('[data-consent]').forEach((button) => {
    button.addEventListener('click', () => saveConsent(button.dataset.consent));
});

document.querySelectorAll('[data-consent-open]').forEach((button) => {
    button.addEventListener('click', showConsent);
});

if (analyticsId) {
    const consent = readConsent();
    if (consent === 'granted') {
        loadAnalytics();
    } else if (consent === null) {
        showConsent();
    }
}

/**
 * Returning visitors are asked once for support on their fifth visit. Only a visit count is
 * stored, and once the prompt has been shown it never comes back, whether it was used or not.
 */
const supportPrompt = document.querySelector('[data-support-prompt]');
const supportVisitsKey = 'syntpris:visits';
const supportShownKey = 'syntpris:support-prompt';
const supportVisitThreshold = 5;

if (supportPrompt) {
    try {
        let visits = Number(localStorage.getItem(supportVisitsKey)) || 0;
        if (!sessionStorage.getItem(supportVisitsKey)) {
            sessionStorage.setItem(supportVisitsKey, '1');
            visits += 1;
            localStorage.setItem(supportVisitsKey, String(visits));
        }

        // Never on top of the consent dialog; the next page view gets it instead.
        const consentOpen = consentBanner && !consentBanner.hidden;
        if (visits >= supportVisitThreshold && !localStorage.getItem(supportShownKey) && !consentOpen) {
            localStorage.setItem(supportShownKey, String(Date.now()));
            setTimeout(() => {
                supportPrompt.hidden = false;
            }, 4000);
        }
    } catch (e) {}

    supportPrompt.querySelectorAll('[data-support-dismiss]').forEach((element) => {
        element.addEventListener('click', () => {
            supportPrompt.hidden = true;
        });
    });
}

/**
 * An item on several marketplaces asks which one to open instead of following its title link.
 */
document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-marketplace-dialog]');
    const dialog = link && document.getElementById(link.dataset.marketplaceDialog);
    if (!dialog || typeof dialog.showModal !== 'function' || event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
    }
    event.preventDefault();

    // A new random order each time, so no marketplace is always on top.
    const list = dialog.querySelector('[data-marketplace-list]');
    const items = [...list.children];
    for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
    }
    list.replaceChildren(...items);

    dialog.showModal();
});

document.querySelectorAll('dialog[id^="marketplaces-"]').forEach((dialog) => {
    // A click on the backdrop lands on the dialog element itself.
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
            dialog.close();
        }
    });
});
